import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { colors } from '../../employees/styles/personalakteStyles';
import PlantafelBoard from './PlantafelBoard';
import PlantafelTasksTab from './PlantafelTasksTab';
import InventoryDashboard from '../../../inventory/InventoryDashboard';

type TabType = 'overview' | 'tasks' | 'inventory' | 'orders' | 'haccp' | 'plantafel';

const TABS: { key: TabType; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'orders', label: 'Orders' },
  { key: 'haccp', label: 'HACCP' },
  { key: 'plantafel', label: 'Plantafel' },
];

const VALID_TABS = TABS.map((t) => t.key);

function OperationsOverview({ onNavigate }: { onNavigate: (tab: TabType) => void }): React.JSX.Element {
  const cards = [
    {
      tab: 'inventory' as TabType,
      title: 'Inventory',
      description: 'Stock levels, counting sessions, and minimum alerts',
      color: '#5271A5',
    },
    {
      tab: 'orders' as TabType,
      title: 'Orders',
      description: 'Supplier orders, deliveries, and receiving',
      color: '#E7007D',
    },
    {
      tab: 'tasks' as TabType,
      title: 'Tasks',
      description: 'Open tasks and assignments',
      color: '#F59E0B',
    },
    {
      tab: 'haccp' as TabType,
      title: 'HACCP',
      description: 'Checklists and compliance',
      color: '#10B981',
    },
    {
      tab: 'plantafel' as TabType,
      title: 'Plantafel',
      description: 'Shift board and scheduling',
      color: '#6366F1',
    },
  ];

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.slate }}>
        Operations Overview
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
        {cards.map((card) => (
          <div
            key={card.tab}
            onClick={() => onNavigate(card.tab)}
            style={{
              padding: '20px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid rgba(255,255,255,0.08)`,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.borderColor = card.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: card.color }} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: colors.slate }}>{card.title}</span>
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>{card.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaceholderTab({ title }: { title: string }): React.JSX.Element {
  return (
    <div style={{ padding: '24px', color: colors.textMuted, fontSize: '14px' }}>
      <h3 style={{ margin: '0 0 8px', color: colors.slate }}>{title}</h3>
      <p style={{ margin: 0 }}>Coming soon.</p>
    </div>
  );
}

export default function OperationsPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab') as TabType;
    return VALID_TABS.includes(tabParam) ? tabParam : 'overview';
  });

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', activeTab);
    setSearchParams(newParams, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OperationsOverview onNavigate={handleTabChange} />;
      case 'tasks':
        return <PlantafelTasksTab />;
      case 'inventory':
        return <InventoryDashboard />;
      case 'orders':
        return <PlaceholderTab title="Orders" />;
      case 'haccp':
        return <PlaceholderTab title="HACCP" />;
      case 'plantafel':
        return <PlantafelBoard />;
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '16px', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100, flexShrink: 0 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.2)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? colors.slate : colors.textMuted,
                    borderRadius: 9999,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = colors.slate;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.textMuted;
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        {renderTabContent()}
      </div>
    </div>
  );
}
